import { isSoulMapMode } from '@/utils/app-mode';

export const SOULMAP_LOGIN_SESSION_KEY = 'soulmap-login-flow';
export const SOULMAP_SESSION_ESTABLISHED_KEY = 'soulmap-session-established';
export const POST_LOGOUT_LOGIN_PATH_KEY = 'post-logout-login-path';

/** Matches /{tenant}/soulmap and sub-routes — not /soulmap-login or /soul-map/home */
const SOULMAP_APP_ROUTE = /\/[^/]+\/soulmap(?:\/|$)/;

export function markSoulMapLoginFlow(): void {
  sessionStorage.setItem(SOULMAP_LOGIN_SESSION_KEY, '1');
}

export function isSoulMapLoginFlow(): boolean {
  return sessionStorage.getItem(SOULMAP_LOGIN_SESSION_KEY) === '1';
}

export function markSoulMapSessionEstablished(): void {
  sessionStorage.setItem(SOULMAP_SESSION_ESTABLISHED_KEY, '1');
}

export function isSoulMapSessionEstablished(): boolean {
  return sessionStorage.getItem(SOULMAP_SESSION_ESTABLISHED_KEY) === '1';
}

/**
 * True only when THIS tab finished Soul Map Google/SSO. Shared persist from
 * another tab, or the pre-popup login-flow flag, is not enough — those would
 * fire LiFT APIs (learning paths, practices) with the other session.
 */
export function isSoulMapAuthenticatedSession(options: {
  isAuthenticated: boolean;
  authMethod?: string | null;
}): boolean {
  if (!options.isAuthenticated || options.authMethod !== 'sso') {
    return false;
  }

  return isSoulMapLoginFlow() && isSoulMapSessionEstablished();
}

export function clearSoulMapLoginFlow(): void {
  sessionStorage.removeItem(SOULMAP_LOGIN_SESSION_KEY);
  sessionStorage.removeItem(SOULMAP_SESSION_ESTABLISHED_KEY);
}

export function markPostLogoutLoginPath(path: string): void {
  sessionStorage.setItem(POST_LOGOUT_LOGIN_PATH_KEY, path);
}

export function clearPostLogoutLoginPath(): void {
  sessionStorage.removeItem(POST_LOGOUT_LOGIN_PATH_KEY);
}

/** True when logout originates from a Soul Map URL (not the session flag alone). */
export function shouldMarkSoulMapPostLogout(pathname?: string): boolean {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  return isSoulMapMode() || isSoulMapLoginPage(path) || isSoulMapAppRoute(path);
}

export function getSoulMapSocialOrganization(): string | null {
  return import.meta.env.VITE_AUTH0_SOCIAL_ORGANIZATION?.trim() || null;
}

export function isSoulMapLoginPage(pathname?: string): boolean {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  // On a Soul Map host the landing renders at the root, so `/` is the same page.
  if (isSoulMapMode() && path === '/') {
    return true;
  }

  return path === '/soulmap-login' || path.startsWith('/soulmap-login?');
}

export function isSoulMapAppRoute(pathname?: string): boolean {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  return SOULMAP_APP_ROUTE.test(path);
}

export function isSoulMapConsumerSession(): boolean {
  return isSoulMapMode() || isSoulMapLoginFlow();
}

/** @deprecated Use isSoulMapAppRoute — kept for callers that need the old name */
export function isSoulMapRoute(pathname?: string): boolean {
  return isSoulMapAppRoute(pathname);
}

export function isSoulMapConsumerContext(pathname?: string): boolean {
  return isSoulMapConsumerSession() && isSoulMapAppRoute(pathname);
}

export function shouldUseSoulMapLogin(pathname?: string): boolean {
  return (
    isSoulMapMode() ||
    isSoulMapLoginFlow() ||
    isSoulMapLoginPage(pathname) ||
    isSoulMapAppRoute(pathname)
  );
}

export function getSoulMapConsumerLandingPath(): string {
  return '/soulmap-login';
}

/** Integrated consumer funnel at /soulmap-login (not tenant /soulmap home). */
export function shouldUseSoulMapConsumerLanding(): boolean {
  return isSoulMapLoginFlow();
}

export function getLoginRedirectPath(pathname?: string): string {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  // Explicit LiFT login URL always wins — even after a Soul Map session.
  if (path === '/login' || path.startsWith('/login/')) {
    return '/login';
  }

  return shouldUseSoulMapLogin(path) ? '/soulmap-login' : '/login';
}

export function getSoulMapHomePath(tenantSlug: string): string {
  return `/${tenantSlug}/soulmap`;
}

export function getLiftHubPath(tenantSlug: string): string {
  if (shouldUseSoulMapConsumerLanding()) {
    return getSoulMapConsumerLandingPath();
  }

  if (isSoulMapMode()) {
    return getSoulMapHomePath(tenantSlug);
  }

  return `/${tenantSlug}/home`;
}

export function isLiftHubActive(pathname: string, hubPath: string): boolean {
  if (isSoulMapConsumerSession() && isSoulMapAppRoute(hubPath)) {
    return pathname.startsWith(hubPath);
  }

  if (hubPath.endsWith('/home')) {
    return pathname === hubPath;
  }

  return pathname.startsWith(hubPath);
}

/** Auth0 only allows origin (or other whitelisted URLs) as returnTo — not deep paths. */
export function getAuth0LogoutReturnTo(
  pathname?: string,
  options?: { markPostLogout?: boolean }
): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const path = pathname ?? window.location.pathname;
  const shouldMark =
    options?.markPostLogout ?? shouldMarkSoulMapPostLogout(path);

  if (shouldMark) {
    markPostLogoutLoginPath('/soulmap-login');
  } else {
    clearPostLogoutLoginPath();
  }

  return window.location.origin;
}
